/** SUNTRONIX ONE — Scan-First Warehouse (mobile field ops).
 *
 *  Five modes: Inward · Rack · Pick · Pack · Dispatch.
 *  One persistent camera preview at the top; every mode is a bottom
 *  sheet with its own inputs.  Each accepted scan flashes green, logs
 *  a `warehouse_scans` row and updates the on-screen counters.
 *
 *  Falls back to manual entry when BarcodeDetector isn't available.
 */
import { SmartLocationInput } from "@/components/warehouse/SmartLocationInput";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Camera, ScanLine, Package, Truck, MapPin, Boxes,
  PackageCheck, RefreshCw, Check, AlertTriangle, Loader2,
} from "lucide-react";

const BARCODE_FORMATS = [
  "code_128", "code_39", "codabar", "code_93",
  "ean_13", "ean_8", "itf", "upc_a", "upc_e",
  "qr_code", "data_matrix", "pdf417", "aztec",
];

const MODES = [
  { key: "inward",   label: "Inward",   icon: Boxes,          color: "text-sky-700" },
  { key: "rack",     label: "Rack",     icon: MapPin,         color: "text-emerald-700" },
  { key: "pick",     label: "Pick",     icon: Package,        color: "text-amber-700" },
  { key: "pack",     label: "Pack",     icon: PackageCheck,   color: "text-purple-700" },
  { key: "dispatch", label: "Dispatch", icon: Truck,          color: "text-red-700" },
];

function useCameraScanner(onCode, active) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [supported, setSupported] = useState(true);
  const [error, setError] = useState(null);
  const lastCodeRef = useRef(null);
  const lastAtRef = useRef(0);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!active) { stop(); return; }
    if (typeof BarcodeDetector === "undefined") {
      setSupported(false); return;
    }
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } }, audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const detector = new BarcodeDetector({ formats: BARCODE_FORMATS });
        const scan = async () => {
          if (!videoRef.current || !streamRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length) {
              const value = codes[0].rawValue?.trim();
              const now = Date.now();
              // De-bounce: same code within 1500ms is ignored
              if (value && (value !== lastCodeRef.current
                              || now - lastAtRef.current > 1500)) {
                lastCodeRef.current = value;
                lastAtRef.current = now;
                onCode(value);
              }
            }
          } catch { /* transient */ }
          rafRef.current = requestAnimationFrame(scan);
        };
        rafRef.current = requestAnimationFrame(scan);
      } catch (e) {
        setError(e.message || "Camera access denied.");
      }
    })();
    return () => { cancelled = true; stop(); };
  }, [active, onCode, stop]);

  return { videoRef, supported, error };
}

// ---------------------------------------------------------------------------
export default function WarehouseScan() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("inward");
  const [cameraOn, setCameraOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);          // {code, at, ok, note}
  const [manual, setManual] = useState("");

  // Mode-specific state
  const [rackWarehouse, setRackWarehouse] = useState("");
  const [rackLocation, setRackLocation] = useState("");
  const [pickShipment, setPickShipment] = useState("");
  const [pickProgress, setPickProgress] = useState(null); // {picked_count,total,assets[]}
  const [packShipment, setPackShipment] = useState("");
  const [packCount, setPackCount] = useState("1");
  const [packWeight, setPackWeight] = useState("");
  const [packDims, setPackDims] = useState("");
  const [dispatchShipment, setDispatchShipment] = useState("");
  const [dispatchAwb, setDispatchAwb] = useState("");
  const [dispatchCourier, setDispatchCourier] = useState("");

  const pushRecent = (entry) => setRecent((r) => [{
    at: new Date().toLocaleTimeString(), ...entry,
  }, ...r].slice(0, 12));

  const refreshPickProgress = useCallback(async (code) => {
    if (!code) return;
    try {
      const { data } = await api.get(
        `/warehouse/shipments/${encodeURIComponent(code)}/progress`);
      setPickProgress(data);
    } catch (e) {
      setPickProgress(null);
      if (e.response?.status !== 404)
        toast.error(e.response?.data?.detail || "Progress load failed");
    }
  }, []);

  useEffect(() => {
    if (mode === "pick" && pickShipment) refreshPickProgress(pickShipment);
    // clear cross-mode state so scans don't leak
  }, [mode, pickShipment, refreshPickProgress]);

  const handleCode = useCallback(async (code) => {
    if (!code || busy) return;
    setBusy(true);
    try {
      if (mode === "inward") {
        const { data } = await api.post("/warehouse/inward", { code });
        pushRecent({ code, ok: true,
                        note: `Received · now awaiting inspection` });
        toast.success(`Received ${data.asset.asset_tag || code}`);
      } else if (mode === "rack") {
        if (!rackWarehouse.trim()) {
          toast.error("Scan or enter the bin / warehouse code first.");
          return;
        }
        const { data } = await api.post("/warehouse/rack", {
          code, warehouse_code: rackWarehouse.trim(),
          location_code: rackLocation.trim() || null,
        });
        pushRecent({ code, ok: true,
                        note: `Racked at ${data.warehouse.code}` });
        toast.success(`Racked ${data.asset.asset_tag || code}`);
      } else if (mode === "pick") {
        if (!pickShipment.trim()) {
          // Treat the first scan of pick mode as the shipment.
          setPickShipment(code);
          await refreshPickProgress(code);
          pushRecent({ code, ok: true, note: "Shipment locked" });
          return;
        }
        const { data } = await api.post("/warehouse/pick", {
          shipment_code: pickShipment.trim(), asset_code: code });
        pushRecent({ code, ok: true,
                        note: (`${data.picked_count}/${data.total} picked`
                                + (data.complete ? " · complete" : "")) });
        toast.success(`Picked ${code} · ${data.picked_count}/${data.total}`);
        await refreshPickProgress(pickShipment);
      } else if (mode === "pack") {
        if (!packShipment.trim()) {
          setPackShipment(code);
          pushRecent({ code, ok: true, note: "Shipment locked · fill parcels" });
          return;
        }
        toast.info("Fill parcels + tap Pack.");
      } else if (mode === "dispatch") {
        if (!dispatchShipment.trim()) {
          setDispatchShipment(code);
          pushRecent({ code, ok: true, note: "Shipment locked · scan AWB" });
        } else if (!dispatchAwb.trim()) {
          setDispatchAwb(code);
          pushRecent({ code, ok: true, note: `AWB captured · ${code}` });
        } else {
          toast.info("AWB already captured — tap Dispatch.");
        }
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail.message || "Scan failed")
                                                  : (detail || "Scan failed");
      pushRecent({ code, ok: false, note: msg });
      toast.error(msg);
    } finally { setBusy(false); }
  }, [busy, mode, rackWarehouse, rackLocation, pickShipment,
       packShipment, dispatchShipment, dispatchAwb, refreshPickProgress]);

  const { videoRef, supported, error } = useCameraScanner(handleCode, cameraOn);

  const submitManual = (e) => {
    e.preventDefault();
    if (manual.trim()) { handleCode(manual.trim()); setManual(""); }
  };

  const commitPack = async () => {
    if (!packShipment.trim()) {
      toast.error("Scan or enter the shipment first."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/warehouse/pack", {
        shipment_code: packShipment.trim(),
        package_count: parseInt(packCount || "1", 10),
        weight_kg: packWeight ? parseFloat(packWeight) : null,
        dimensions: packDims || null,
      });
      pushRecent({ code: packShipment, ok: true,
                      note: `Packed · ${data.packages.count} pkg` });
      toast.success("Packed — ready for dispatch.");
      setPackShipment(""); setPackCount("1");
      setPackWeight(""); setPackDims("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "object" ? (detail.message || "Pack failed")
                                                    : (detail || "Pack failed"));
    } finally { setBusy(false); }
  };

  const commitDispatch = async () => {
    if (!dispatchShipment.trim() || !dispatchAwb.trim()) {
      toast.error("Need both shipment and AWB."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/warehouse/dispatch", {
        shipment_code: dispatchShipment.trim(),
        awb: dispatchAwb.trim(),
        courier: dispatchCourier.trim() || null,
      });
      pushRecent({ code: dispatchShipment, ok: true,
                      note: `Dispatched · AWB ${data.shipment.awb}` });
      toast.success(`Dispatched · AWB ${data.shipment.awb}`);
      setDispatchShipment(""); setDispatchAwb(""); setDispatchCourier("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "object" ? (detail.message || "Dispatch failed")
                                                    : (detail || "Dispatch failed"));
    } finally { setBusy(false); }
  };

  const currentMode = MODES.find((m) => m.key === mode);

  return (
    <div className="max-w-md mx-auto space-y-4 pb-16"
          data-testid="warehouse-scan-page">
      {/* Header */}
      <header className="flex items-center gap-3 pt-2">
        <button onClick={() => navigate(-1)}
             className="text-slate-500 hover:text-slate-900"
             data-testid="warehouse-scan-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            ONE Mobile · Warehouse
          </div>
          <h1 className="text-xl font-heading font-semibold text-slate-900 tracking-tight">
            Scan-first ops
          </h1>
        </div>
      </header>

      {/* Mode selector */}
      <div className="grid grid-cols-5 gap-1"
             data-testid="warehouse-scan-modes">
        {MODES.map((m) => {
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => { setMode(m.key); }}
              data-testid={`warehouse-mode-${m.key}`}
              className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-[10px] uppercase tracking-wider font-semibold transition
                ${active ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
              <m.icon className="h-4 w-4" />
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Camera preview */}
      <div className="st-panel p-3 space-y-3">
        <div className="relative rounded-md overflow-hidden bg-slate-950 aspect-[4/3]">
          <video ref={videoRef} playsInline muted
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="warehouse-scan-video" />
          {!cameraOn && (
            <div className="absolute inset-0 grid place-items-center text-slate-400 text-[12px]">
              <div className="flex flex-col items-center gap-2">
                <ScanLine className="h-8 w-8" />
                <div>Tap Start camera</div>
              </div>
            </div>
          )}
          {cameraOn && (
            <div className="absolute inset-0 pointer-events-none border-2 border-emerald-400/60 rounded-md m-6 animate-pulse" />
          )}
          {busy && (
            <div className="absolute inset-0 grid place-items-center bg-slate-950/60 text-white text-[12px]"
                  data-testid="warehouse-scan-busy">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Working…
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!cameraOn ? (
            <Button onClick={() => setCameraOn(true)}
              data-testid="warehouse-scan-start"
              className="flex-1">
              <Camera className="h-3.5 w-3.5 mr-1.5" /> Start camera
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setCameraOn(false)}
              data-testid="warehouse-scan-stop"
              className="flex-1">
              Stop camera
            </Button>
          )}
        </div>
        {!supported && (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Live scan not supported — use manual entry below.
          </div>
        )}
        {error && (
          <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
            {error}
          </div>
        )}
      </div>

      {/* Mode-specific inputs */}
      <div className="st-panel p-3 space-y-3">
        <div className={`text-[10px] uppercase tracking-widest font-semibold ${currentMode.color}`}>
          {currentMode.label} mode
        </div>

        {mode === "inward" && (
          <div className="text-[12px] text-slate-600" data-testid="warehouse-inward-help">
            Scan or type an asset serial / tag.  Each scan marks the asset
            <span className="font-mono"> awaiting_inspection </span>
            and clears the previous employee.
          </div>
        )}

        {mode === "rack" && (
          <div className="space-y-2" data-testid="warehouse-rack-inputs">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Bin / warehouse code</div>
              <Input value={rackWarehouse}
                onChange={(e) => setRackWarehouse(e.target.value)}
                placeholder="e.g. WH-EU-01"
                data-testid="warehouse-rack-warehouse" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Location (aisle / shelf)</div>
              <SmartLocationInput onResolved={(l) => { setRackLocation(l.code); if (!rackWarehouse.trim()) setRackWarehouse(l.code.split("-")[0]); }} placeholder="Scan or type the location — any format" testid="warehouse-rack-location" />
            </label>
            <div className="text-[11px] text-slate-500">
              Next scan sets the asset&apos;s warehouse + shelf.
            </div>
          </div>
        )}

        {mode === "pick" && (
          <div className="space-y-2" data-testid="warehouse-pick-inputs">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Shipment code</div>
              <div className="flex gap-2">
                <Input value={pickShipment}
                  onChange={(e) => setPickShipment(e.target.value)}
                  placeholder="Scan or type SHP-..."
                  data-testid="warehouse-pick-shipment" />
                <Button variant="outline" size="sm"
                  onClick={() => refreshPickProgress(pickShipment)}
                  disabled={!pickShipment.trim()}
                  data-testid="warehouse-pick-refresh">
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
            </label>
            {pickProgress && (
              <div className="rounded border border-slate-200 p-2"
                    data-testid="warehouse-pick-progress">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] text-slate-500">
                    {pickProgress.shipment_code} · {pickProgress.status}
                  </div>
                  <div className="text-[13px] font-semibold tabular-nums">
                    {pickProgress.picked_count} / {pickProgress.total}
                  </div>
                </div>
                <div className="h-1.5 bg-slate-100 rounded mt-1 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${Math.round(
                          (pickProgress.picked_count / Math.max(pickProgress.total, 1)) * 100)}%` }} />
                </div>
                <div className="mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                  {(pickProgress.assets || []).map((a) => (
                    <div key={a.id}
                          className={`text-[11px] flex items-center justify-between rounded px-1.5 py-0.5
                          ${a.picked ? "bg-emerald-50 text-emerald-800"
                                        : "text-slate-600"}`}
                          data-testid={`warehouse-pick-row-${a.id}`}>
                      <span className="font-mono">{a.asset_tag} · {a.serial}</span>
                      {a.picked ? <Check className="h-3 w-3" /> : null}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="text-[11px] text-slate-500">
              Scan each asset — off-manifest scans are rejected.
            </div>
          </div>
        )}

        {mode === "pack" && (
          <div className="space-y-2" data-testid="warehouse-pack-inputs">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Shipment</div>
              <Input value={packShipment}
                onChange={(e) => setPackShipment(e.target.value)}
                placeholder="Scan or type SHP-..."
                data-testid="warehouse-pack-shipment" />
            </label>
            <div className="grid grid-cols-3 gap-2">
              <label>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Boxes</div>
                <Input type="number" min="1" value={packCount}
                  onChange={(e) => setPackCount(e.target.value)}
                  data-testid="warehouse-pack-count" />
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Weight (kg)</div>
                <Input type="number" step="0.1" value={packWeight}
                  onChange={(e) => setPackWeight(e.target.value)}
                  placeholder="4.2"
                  data-testid="warehouse-pack-weight" />
              </label>
              <label>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Dims</div>
                <Input value={packDims}
                  onChange={(e) => setPackDims(e.target.value)}
                  placeholder="40x30x15"
                  data-testid="warehouse-pack-dims" />
              </label>
            </div>
            <Button onClick={commitPack} disabled={busy || !packShipment.trim()}
              className="w-full"
              data-testid="warehouse-pack-commit">
              <PackageCheck className="h-3.5 w-3.5 mr-1.5" />
              Confirm pack
            </Button>
          </div>
        )}

        {mode === "dispatch" && (
          <div className="space-y-2" data-testid="warehouse-dispatch-inputs">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Shipment</div>
              <Input value={dispatchShipment}
                onChange={(e) => setDispatchShipment(e.target.value)}
                placeholder="Scan or type SHP-..."
                data-testid="warehouse-dispatch-shipment" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">AWB</div>
              <Input value={dispatchAwb}
                onChange={(e) => setDispatchAwb(e.target.value)}
                placeholder="Scan or type courier AWB"
                data-testid="warehouse-dispatch-awb" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Courier (optional)</div>
              <Input value={dispatchCourier}
                onChange={(e) => setDispatchCourier(e.target.value)}
                placeholder="DHL / FedEx / Aramex…"
                data-testid="warehouse-dispatch-courier" />
            </label>
            <Button onClick={commitDispatch}
              disabled={busy || !dispatchShipment.trim() || !dispatchAwb.trim()}
              className="w-full"
              data-testid="warehouse-dispatch-commit">
              <Truck className="h-3.5 w-3.5 mr-1.5" /> Dispatch
            </Button>
          </div>
        )}
      </div>

      {/* Manual entry — always available */}
      <div className="st-panel p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          Manual entry
        </div>
        <form onSubmit={submitManual} className="flex gap-2">
          <Input value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="Serial · tag · shipment · AWB"
            data-testid="warehouse-scan-manual" />
          <Button type="submit" disabled={!manual.trim() || busy}
            data-testid="warehouse-scan-manual-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Scan"}
          </Button>
        </form>
      </div>

      {/* Recent scans */}
      <div className="st-panel p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
          Recent scans
        </div>
        {recent.length === 0 && (
          <div className="text-[12px] text-slate-400 py-4 text-center"
                data-testid="warehouse-scan-empty">
            No scans yet — start the camera or type below.
          </div>
        )}
        <div className="space-y-1">
          {recent.map((r, i) => (
            <div key={i}
                  data-testid={`warehouse-scan-log-${i}`}
                  className={`flex items-center gap-2 text-[11px] rounded px-2 py-1
                  ${r.ok ? "bg-emerald-50 text-emerald-800"
                            : "bg-red-50 text-red-800"}`}>
              {r.ok ? <Check className="h-3 w-3" />
                     : <AlertTriangle className="h-3 w-3" />}
              <span className="font-mono">{r.code}</span>
              <span className="ml-auto text-[10px] opacity-70">{r.at}</span>
              <span className="basis-full text-[10px] opacity-80 pl-4">
                {r.note}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
